import {
  CheckCircleFilled,
  CloseCircleFilled,
  ReloadOutlined,
  SearchOutlined,
  TeamOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Input,
  message,
  Modal,
  Pagination,
  Popconfirm,
  Progress,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from "antd";
import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CREATE_jwel, CREATE_jwel1 } from "../../Config/Config";

const ROLE_COLORS = {
  ADMINISTRATOR: { bg: "#eef0fb", fg: "#0f2460" },
  OPERATOR: { bg: "#e3f5f3", fg: "#0f847e" },
  ORDERS: { bg: "#f2e9f7", fg: "#7b3fa0" },
  DEFAULT: { bg: "#fbf3de", fg: "#a97e0b" },
};

// Roles that should never surface in this screen — the owner's own
// admin row(s), plus any system/origin rows. Checked as an exact,
// upper-cased match against ROLE.
const EXCLUDED_ROLES = ["ADMIN", "MAINADMIN", "ORIGIN"];

const G = {
  page: {
    backgroundColor: "#f0f2f5",
    minHeight: "100vh",
    padding: "18px 22px",
  },
  statsRow: {
    display: "flex",
    gap: 16,
    flexWrap: "wrap",
    marginBottom: 18,
  },
  statCard: (gradient) => ({
    flex: "1 1 180px",
    minWidth: 180,
    borderRadius: 14,
    padding: "16px 18px",
    background: gradient,
    color: "#fff",
    boxShadow: "0 6px 18px -8px rgba(15,36,96,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  }),
  statNumber: { fontSize: 28, fontWeight: 800, lineHeight: 1 },
  statLabel: {
    fontSize: 12.5,
    opacity: 0.85,
    marginTop: 4,
    letterSpacing: 0.3,
  },
  card: {
    background: "#fff",
    borderRadius: 14,
    boxShadow: "0 4px 24px rgba(15,36,96,0.13)",
    overflow: "hidden",
  },
  toolbar: {
    padding: "14px 18px",
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 10,
    borderBottom: "1px solid #eef0ec",
  },
  toolbarHeader: {
    background: "linear-gradient(90deg, #0f2460 0%, #1a3a8f 100%)",
    padding: "14px 18px",
    color: "#fff",
    fontWeight: 700,
    fontSize: 15,
    textTransform: "uppercase",
    letterSpacing: 1,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 10,
  },
};

const roleBadgeStyle = (role) => {
  const key = role?.toUpperCase();
  const c = ROLE_COLORS[key] || ROLE_COLORS.DEFAULT;
  return { background: c.bg, color: c.fg, border: "none", fontWeight: 600 };
};

// ===== Responsive CSS: table stays for desktop, cards take over for tablet/mobile =====
// (kept in one <style> tag so no build-config / styled-components dependency is needed)
const RESPONSIVE_CSS = `
.uam-desktop-table { display: block; }
.uam-card-grid { display: none; }

.uam-toolbar-input,
.uam-toolbar-select {
    width: 220px;
}

@media (max-width: 992px) {
    .uam-desktop-table { display: none; }
    .uam-card-grid { display: grid; }

    .uam-toolbar-input,
    .uam-toolbar-select {
        width: 100% !important;
        flex: 1 1 0;
    }
    .uam-toolbar-actions {
        width: 100%;
        justify-content: flex-end;
    }
    .uam-header-actions {
        width: 100%;
    }
    .uam-header-actions .ant-space {
        width: 100%;
    }
    .uam-header-actions button {
        flex: 1 1 0;
    }
}

@media (max-width: 992px) and (min-width: 577px) {
    .uam-card-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 576px) {
    .uam-card-grid {
        grid-template-columns: 1fr;
    }
    .uam-stat-card {
        flex: 1 1 100% !important;
    }
}
`;

const UserAccountManager = () => {
  const tenantName = localStorage.getItem("tenantName");
  const username = localStorage.getItem("UName");

  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resolvingOwner, setResolvingOwner] = useState(true);
  const [ownerRecord, setOwnerRecord] = useState(null);
  const resolvedTenantName = ownerRecord?.TENANTNAME || tenantName;

  // ---- filters ----
  const [searchText, setSearchText] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // ---- individual toggle ----
  const [togglingKey, setTogglingKey] = useState(null);

  // ---- selection ----
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // ---- bulk operation state ----
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({
    done: 0,
    total: 0,
    failed: [],
  });
  const [bulkLabel, setBulkLabel] = useState("");

  // ===== resolve owner/tenant context, same pattern as UserAccounts.jsx =====
  const resolveOwnerRecord = useCallback(async () => {
    if (!tenantName) {
      message.error("No tenant context found — please log in again");
      setResolvingOwner(false);
      return null;
    }
    setResolvingOwner(true);
    try {
      const res = await axios.get(
        `${CREATE_jwel1}/api/Tenant/GetDataFromGivenTableNameWithWhere?tableName=ClientDetails&where=USERNAME='${username}'`,
        { headers: { tenantName: username } },
      );
      const data = Array.isArray(res.data)
        ? res.data
        : res.data
          ? [res.data]
          : [];
      const record =
        data.find((r) => r.ROLE?.toUpperCase().includes("ADMIN")) || data[0];
      if (!record) {
        message.error("Could not resolve firm details for this tenant");
        setOwnerRecord(null);
        return null;
      }
      setOwnerRecord(record);
      return record;
    } catch (error) {
      console.error("Error resolving firm details:", error);
      message.error("Could not resolve firm details for this tenant");
      setOwnerRecord(null);
      return null;
    } finally {
      setResolvingOwner(false);
    }
  }, [tenantName]);

  const fetchData = (tenant) => {
    setLoading(true);
    setPage(1);
    setSelectedRowKeys([]);
    const where = encodeURIComponent(`tenantname='${tenant}'`);
    axios
      .get(
        `${CREATE_jwel1}/api/Tenant/GetDataFromGivenTableNameWithWhere?tableName=ClientDetails&where=${where}`,
        { headers: { tenantName } },
      )
      .then((res) =>
        setRawData(
          (res.data || []).map((item, idx) => ({ ...item, key: idx + 1 })),
        ),
      )
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    (async () => {
      const record = await resolveOwnerRecord();
      const tenant = record?.TENANTNAME || tenantName;
      if (tenant) fetchData(tenant);
    })();
  }, [resolveOwnerRecord]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = () => {
    if (resolvedTenantName) fetchData(resolvedTenantName);
  };

  // ===== derived: filtered list (excludes ADMIN/MAINADMIN/ORIGIN rows) =====
  const filteredData = useMemo(() => {
    return rawData.filter((item) => {
      const role = String(item.ROLE ?? "").toUpperCase();
      if (EXCLUDED_ROLES.includes(role)) return false;
      if (
        searchText &&
        !item.USERNAME?.toLowerCase().includes(searchText.toLowerCase())
      )
        return false;
      if (roleFilter !== "all" && role !== roleFilter) return false;
      const isActive = String(item.DELIVERYCHARGES) === "1";
      if (statusFilter === "active" && !isActive) return false;
      if (statusFilter === "inactive" && isActive) return false;
      return true;
    });
  }, [rawData, searchText, roleFilter, statusFilter]);

  const roleOptions = useMemo(() => {
    const roles = new Set(
      rawData
        .filter((r) => !EXCLUDED_ROLES.includes(String(r.ROLE ?? "").toUpperCase()))
        .map((r) => r.ROLE?.toUpperCase())
        .filter(Boolean),
    );
    return Array.from(roles);
  }, [rawData]);

  const paginatedData = filteredData.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  const activeCount = filteredData.filter(
    (r) => String(r.DELIVERYCHARGES) === "1",
  ).length;
  const inactiveCount = filteredData.length - activeCount;

  // ===== common single-user update call — identical to UserAccounts.jsx =====
  const updateDeliveryCharges = async (record, newValue) => {
    await axios.post(
      `${CREATE_jwel1}/api/Tenant/UpdateWithWhere`,
      {
        tableName: "ClientDetails",
        updateString: `DELIVERYCHARGES='${newValue}'`,
        whereString: `USERNAME='${record.USERNAME}' AND TENANTNAME='${record.TENANTNAME}' AND FIRMNAME='${record.FIRMNAME}'`,
      },
      { headers: { tenantName } },
    );
  };

  // ===== individual switch — instant, no loop =====
  const handleStatusToggle = async (record, checked) => {
    const rowKey = record.key;
    const previousValue = record.DELIVERYCHARGES;
    const newValue = checked ? "1" : "0";

    setTogglingKey(rowKey);
    setRawData((prev) =>
      prev.map((r) =>
        r.key === rowKey ? { ...r, DELIVERYCHARGES: newValue } : r,
      ),
    );

    try {
      await updateDeliveryCharges(record, newValue);
      message.success(
        `${record.USERNAME} marked as ${checked ? "Active" : "Inactive"}`,
      );
    } catch (error) {
      console.error("Error updating status:", error);
      message.error("Could not update status");
      setRawData((prev) =>
        prev.map((r) =>
          r.key === rowKey ? { ...r, DELIVERYCHARGES: previousValue } : r,
        ),
      );
    } finally {
      setTogglingKey(null);
    }
  };

  // ===== bulk update — loops the SAME update API call, one request per user =====
  const runBulkUpdate = async (records, newValue, label) => {
    if (records.length === 0) {
      message.info("No users to update for this action");
      return;
    }
    setBulkLabel(label);
    setBulkLoading(true);
    setBulkProgress({ done: 0, total: records.length, failed: [] });

    const failed = [];
    for (let i = 0; i < records.length; i++) {
      const record = records[i];
      try {
        await updateDeliveryCharges(record, newValue);
        setRawData((prev) =>
          prev.map((r) =>
            r.key === record.key ? { ...r, DELIVERYCHARGES: newValue } : r,
          ),
        );
      } catch (error) {
        console.error(`Failed updating ${record.USERNAME}:`, error);
        failed.push(record.USERNAME);
      }
      setBulkProgress((prev) => ({ ...prev, done: i + 1, failed }));
    }

    setBulkLoading(false);
    setSelectedRowKeys([]);

    if (failed.length === 0) {
      message.success(`${records.length} user(s) updated successfully`);
    } else {
      message.warning(
        `${records.length - failed.length} succeeded, ${failed.length} failed: ${failed.join(", ")}`,
      );
    }
  };

  const handleActivateAll = () =>
    runBulkUpdate(filteredData, "1", "Activating all users");
  const handleDeactivateAll = () =>
    runBulkUpdate(filteredData, "0", "Deactivating all users");

  const selectedRecords = filteredData.filter((r) =>
    selectedRowKeys.includes(r.key),
  );
  const handleActivateSelected = () =>
    runBulkUpdate(
      selectedRecords,
      "1",
      `Activating ${selectedRecords.length} selected user(s)`,
    );
  const handleDeactivateSelected = () =>
    runBulkUpdate(
      selectedRecords,
      "0",
      `Deactivating ${selectedRecords.length} selected user(s)`,
    );

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    preserveSelectedRowKeys: true,
  };

  const toggleCardSelection = (key, checked) => {
    setSelectedRowKeys((prev) =>
      checked ? [...prev, key] : prev.filter((k) => k !== key),
    );
  };

  const columns = [
    {
      title: "S.No",
      key: "sno",
      width: 60,
      render: (_, __, i) => (page - 1) * pageSize + i + 1,
    },
    { title: "Username", dataIndex: "USERNAME", key: "USERNAME" },
    {
      title: "Role",
      dataIndex: "ROLE",
      key: "ROLE",
      render: (role) => <Tag style={roleBadgeStyle(role)}>{role}</Tag>,
    },
    { title: "Tenant", dataIndex: "TENANTNAME", key: "TENANTNAME" },
    {
      title: "Status",
      dataIndex: "DELIVERYCHARGES",
      key: "status",
      width: 130,
      render: (value, record) => {
        const isActive = String(value) === "1";
        return (
          <Space size={8}>
            <Switch
              checked={isActive}
              loading={togglingKey === record.key}
              disabled={bulkLoading}
              onChange={(checked) => handleStatusToggle(record, checked)}
            />
            {isActive ? (
              <CheckCircleFilled style={{ color: "#189a4d" }} />
            ) : (
              <CloseCircleFilled style={{ color: "#c9463c" }} />
            )}
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: isActive ? "#189a4d" : "#c9463c",
              }}
            >
              {isActive ? "Active" : "Inactive"}
            </span>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={G.page}>
      <style>{RESPONSIVE_CSS}</style>

      {/* ---- colorful summary stat cards ---- */}
      <div style={G.statsRow}>
        <div
          className="uam-stat-card"
          style={G.statCard(
            "linear-gradient(135deg, #967f01 0%, #a5932d 100%)",
          )}
        >
          <div>
            <div style={G.statNumber}>{filteredData.length}</div>
            <div style={G.statLabel}>TOTAL USERS</div>
          </div>
          <TeamOutlined style={{ fontSize: 30, opacity: 0.6 }} />
        </div>
        <div
          className="uam-stat-card"
          style={G.statCard(
            "linear-gradient(135deg, #159895 0%, #0c7a4b 100%)",
          )}
        >
          <div>
            <div style={G.statNumber}>{activeCount}</div>
            <div style={G.statLabel}>ACTIVE</div>
          </div>
          <CheckCircleFilled style={{ fontSize: 28, opacity: 0.6 }} />
        </div>
        <div
          className="uam-stat-card"
          style={G.statCard(
            "linear-gradient(135deg, #c9463c 0%, #8e2f28 100%)",
          )}
        >
          <div>
            <div style={G.statNumber}>{inactiveCount}</div>
            <div style={G.statLabel}>INACTIVE</div>
          </div>
          <CloseCircleFilled style={{ fontSize: 28, opacity: 0.6 }} />
        </div>
      </div>

      <div style={G.card}>
        <div className="uam-header-actions" style={G.toolbarHeader}>
          <span>User Account Manager</span>
          <Space wrap>
            <Popconfirm
              title="Activate all users?"
              description={`This will call the update API for all ${filteredData.length} filtered user(s), one by one.`}
              onConfirm={handleActivateAll}
              okText="Yes, activate all"
              cancelText="Cancel"
              disabled={bulkLoading || filteredData.length === 0}
            >
              <Button
                type="primary"
                style={{ background: "#189a4d", borderColor: "#189a4d" }}
                disabled={bulkLoading || filteredData.length === 0}
              >
                Activate All
              </Button>
            </Popconfirm>
            <Popconfirm
              title="Deactivate all users?"
              description={`This will call the update API for all ${filteredData.length} filtered user(s), one by one.`}
              onConfirm={handleDeactivateAll}
              okText="Yes, deactivate all"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
              disabled={bulkLoading || filteredData.length === 0}
            >
              <Button
                danger
                disabled={bulkLoading || filteredData.length === 0}
              >
                Deactivate All
              </Button>
            </Popconfirm>
          </Space>
        </div>

        {/* ---- filters + selection-based bulk actions ---- */}
        <div className="uam-toolbar" style={G.toolbar}>
          <Input
            className="uam-toolbar-input"
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search username..."
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(1);
            }}
            style={{ width: 220 }}
          />
          <Select
            className="uam-toolbar-select"
            value={roleFilter}
            onChange={(v) => {
              setRoleFilter(v);
              setPage(1);
            }}
            style={{ width: 170 }}
            options={[
              { value: "all", label: "All Roles" },
              ...roleOptions.map((r) => ({ value: r, label: r })),
            ]}
          />
          <Select
            className="uam-toolbar-select"
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v);
              setPage(1);
            }}
            style={{ width: 150 }}
            options={[
              { value: "all", label: "All Status" },
              { value: "active", label: "Active only" },
              { value: "inactive", label: "Inactive only" },
            ]}
          />

          <Tooltip title="Refresh list">
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
            />
          </Tooltip>

          <div style={{ flex: 1 }} />

          {selectedRowKeys.length > 0 && (
            <Space wrap className="uam-toolbar-actions">
              <span style={{ fontSize: 13, color: "#8B8496" }}>
                <UserSwitchOutlined /> {selectedRowKeys.length} selected
              </span>
              <Button
                size="small"
                style={{
                  background: "#e3f5f3",
                  color: "#0f847e",
                  border: "none",
                }}
                onClick={handleActivateSelected}
                disabled={bulkLoading}
              >
                Activate Selected
              </Button>
              <Button
                size="small"
                style={{
                  background: "#fdecea",
                  color: "#c9463c",
                  border: "none",
                }}
                onClick={handleDeactivateSelected}
                disabled={bulkLoading}
              >
                Deactivate Selected
              </Button>
            </Space>
          )}
        </div>

        {/* ---- DESKTOP: table (unchanged) ---- */}
        <div className="uam-desktop-table" style={{ padding: "10px 18px" }}>
          <Table
            columns={columns}
            dataSource={paginatedData}
            rowKey="key"
            pagination={false}
            size="middle"
            rowSelection={rowSelection}
            loading={loading || resolvingOwner}
          />
        </div>

        {/* ---- TABLET / MOBILE: card grid ---- */}
        <div
          className="uam-card-grid"
          style={{ padding: "14px 18px", gap: 14 }}
        >
          {(loading || resolvingOwner) && (
            <div
              style={{
                gridColumn: "1 / -1",
                textAlign: "center",
                padding: "30px 0",
                color: "#8B8496",
              }}
            >
              Loading users...
            </div>
          )}
          {!(loading || resolvingOwner) && paginatedData.length === 0 && (
            <div
              style={{
                gridColumn: "1 / -1",
                textAlign: "center",
                padding: "30px 0",
                color: "#8B8496",
              }}
            >
              No users match the current filters
            </div>
          )}
          {!(loading || resolvingOwner) &&
            paginatedData.map((record) => {
              const isActive = String(record.DELIVERYCHARGES) === "1";
              const isSelected = selectedRowKeys.includes(record.key);
              return (
                <div
                  key={record.key}
                  style={{
                    border: isSelected
                      ? "1.5px solid #1a3a8f"
                      : "1px solid #eef0ec",
                    borderRadius: 12,
                    padding: "14px 16px",
                    background: isSelected ? "#f5f7ff" : "#fff",
                    boxShadow: "0 2px 10px rgba(15,36,96,0.06)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 10,
                      }}
                    >
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) =>
                          toggleCardSelection(record.key, e.target.checked)
                        }
                      />
                      <div>
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: 15,
                            color: "#0f2460",
                          }}
                        >
                          {record.USERNAME}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: "#8B8496",
                            marginTop: 2,
                          }}
                        >
                          {record.TENANTNAME}
                        </div>
                      </div>
                    </div>
                    <Tag style={roleBadgeStyle(record.ROLE)}>{record.ROLE}</Tag>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      paddingTop: 10,
                      borderTop: "1px dashed #eef0ec",
                    }}
                  >
                    <Space size={8}>
                      <Switch
                        checked={isActive}
                        loading={togglingKey === record.key}
                        disabled={bulkLoading}
                        onChange={(checked) =>
                          handleStatusToggle(record, checked)
                        }
                      />
                      {isActive ? (
                        <CheckCircleFilled style={{ color: "#189a4d" }} />
                      ) : (
                        <CloseCircleFilled style={{ color: "#c9463c" }} />
                      )}
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: isActive ? "#189a4d" : "#c9463c",
                        }}
                      >
                        {isActive ? "Active" : "Inactive"}
                      </span>
                    </Space>
                  </div>
                </div>
              );
            })}
        </div>

        <div style={{ padding: "0 18px 14px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 14,
              flexWrap: "wrap",
            }}
          >
            <Pagination
              current={page}
              pageSize={pageSize}
              total={filteredData.length}
              showSizeChanger
              pageSizeOptions={["10", "20", "50", "100"]}
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${total} items`
              }
              onChange={(newPage, newPageSize) => {
                setPage(newPage);
                setPageSize(newPageSize);
              }}
              simple={window.innerWidth <= 576}
            />
          </div>
        </div>
      </div>

      {/* ---- bulk progress modal ---- */}
      <Modal
        open={bulkLoading}
        closable={false}
        footer={null}
        centered
        maskClosable={false}
        width={420}
      >
        <div style={{ textAlign: "center", padding: "10px 4px" }}>
          <h3 style={{ marginBottom: 6 }}>{bulkLabel}</h3>
          <p style={{ color: "#8B8496", fontSize: 13, marginBottom: 18 }}>
            Updating {bulkProgress.done} / {bulkProgress.total} users...
          </p>
          <Progress
            percent={Math.round(
              (bulkProgress.done / (bulkProgress.total || 1)) * 100,
            )}
            status={bulkProgress.failed.length > 0 ? "exception" : "active"}
            strokeColor={{ "0%": "#159895", "100%": "#0c7a4b" }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default UserAccountManager;